<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { $router } from '@/router'
import { dialog } from '@/common/dialog'
import { $bus } from '@/common/events'
import { env } from '@/secrets/env'
import { defaultServiceOptions } from '@/common/config'
import { createSideMenu, createDebugMenu } from '@/menu/main'
import { useMenu, useSubmenu, useTopBar } from '@/composables/menu'
import { useSettings } from '@/composables/settings'
import { useAuthState } from '@/composables/auth'
import { LogService } from '@/services/LogService.js'

const { menuVisible, menuSelected, menuItems, isServiceMenu } = useMenu();
const { submenuVisible, submenuSelected, submenuItems } = useSubmenu();
const { topbarVisible, topbarSelected, topbarItems } = useTopBar();

const useCase = ref(null);

const route = useRoute();

const logService = new LogService();

let optService = defaultServiceOptions();

//User icon
const user = ref({
  initials: "L"
});

//Define menu items
const items = ref(createSideMenu());

//Define debug menu
const debug = ref(createDebugMenu(null, null, { optService }));

defineExpose({ refreshButtons });

watch(() => route.path, (newValue, oldValue) => {
  refreshButtons();
});

onMounted(() => {
  useCase.value = env.VITE_USE_CASE;

  refreshButtons();

  //Returned back from control panel
  if (route.query?.forceLogout == "true") {
    logout();
  }
});

function refreshButtons() {
  //Helper functions
  const item = (name) => items.value.find(x => x.name == name) || debug.value.find(x => x.name == name) || { };

  try {
    let isServiceEntry = route.query?.service == "true" || isServiceMenu.value;

    //Defaults
    item("settings").visible = false;
    item("calibration").visible = false;
    item("catalog").visible = false;
    item("gloves").visible = false;
    item("gloves-stock").visible = false;
    item("control-panel").visible = false;
    item("admin").visible = false;

    const { settings } = useSettings();
    let mode = settings.value?.cabinet?.mode || useCase.value; //null or "gloves"

    const authStore = useAuthState();
    let permissions = authStore.permissions.value || [];

    item("control-panel").visible = !!authStore.jwt.value && permissions.includes("controlPanel.login");
    item("admin").visible = permissions.includes("cabinet.admin") || isServiceEntry;
    item("settings").visible = permissions.includes("cabinet.service") || isServiceEntry;
    item("calibration").visible = permissions.includes("cabinet.service") || isServiceEntry;

    item("catalog").visible = (mode != "gloves" && (permissions.includes("cabinet.viewArticle") || permissions.includes("cabinet.useArticle"))) && !isServiceEntry;
    item("gloves").visible = (mode == "gloves" && (permissions.includes("useCase.gloves.pickupItem") || permissions.includes("useCase.gloves.returnItem"))) && !isServiceEntry;
    item("gloves-stock").visible = (mode == "gloves" && (permissions.includes("useCase.gloves.load") || permissions.includes("useCase.gloves.change"))) && !isServiceEntry;

    let username = authStore.user.value?.username;
    if (username?.length > 0) {
      user.value.initials = username[0].toUpperCase();
    }
  }
  catch (err) {
    console.error("[side menu]", "refreshButtons", err.message);
  }

  let iCount = items.value.length ? items.value.filter(x => x.visible).length : 0;
  let dCount = debug.value.length ? debug.value.filter(x => x.visible).length : 0;
  menuVisible.value = (iCount + dCount) > 1;
  if (!menuVisible.value) {
    topbarVisible.value = true;
  }
}

async function logout(e) {
  try {
    //Hide menu
    useMenu(false);
    useSubmenu(false);
    useTopBar(false);
    
    //Redirect to login page
    $router.push({ name: "Login" });
  }
  catch (err) {
    console.error(err);
  }
}
</script>

<template>
  <div class="side-menu flex h-screen w-16 flex-col justify-between border-e dark:border-e-gray-700 bg-white dark:bg-gray-800">
    <div>
      <!-- User -->
      <div class="inline-flex h-16 w-16 items-center justify-center">
        <span class="grid h-10 w-10 place-content-center rounded-lg bg-gray-100 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-500">{{ user.initials }}</span>
      </div>

      <!-- Menu -->
      <div class="border-t border-gray-100 dark:border-gray-800">
        <div class="px-2">
          <ul class="space-y-1 border-t border-gray-100 dark:border-gray-700 pt-4">

            <li v-for="(item, index) in items" :key="item.id + '-' + index">
              <a href="#" class="group menu-item aspect-square h-12" :class="{ 'menu-item-selected': item.selected || item.name == menuSelected }" :accesskey="item.accesskey" @click.prevent="item.click" v-if="item.visible">
                <i class="fa" :class="item.icon"></i>
                <!-- <span v-if="item.tooltip" class="tooltip">{{ item.tooltip }}</span> -->
              </a>
            </li>

          </ul>
        </div>
      </div>
    </div>

    <div class="sticky inset-x-0 bottom-0 bg-white dark:bg-gray-800">

      <!-- Debug menu -->
      <div class="border-b border-gray-100 dark:border-gray-800" v-if="debug && debug.length">
        <div class="px-2">
          <ul class="space-y-1 pb-2">

            <li v-for="(item, index) in debug" :key="item.id + '-' + index">
              <a href="#" class="group menu-item aspect-square h-12" :class="{ 'menu-item-selected': item.selected || item.name == menuSelected }" :accesskey="item.accesskey" @click.prevent="item.click" v-if="item.visible">
                <i class="fa" :class="item.icon"></i>
                <!-- <span v-if="item.tooltip" class="tooltip">{{ item.tooltip }}</span> -->
              </a>
            </li>

          </ul>
        </div>
      </div>

      <!-- Log out -->
      <!-- <div class="p-2">
        <a href="#" class="group menu-item" @click.prevent="logout">
          <i class="fa fa-arrow-right-from-bracket"></i>
        </a>
      </div> -->
      
    </div>
  </div>
</template>

<style scoped>
.tooltip {
  /* @apply absolute start-full top-1/2 ms-4 -translate-y-1/2 rounded bg-gray-900 px-2 py-1.5 text-xs font-medium text-white opacity-0 group-hover:opacity-100 */
  @apply absolute start-full top-1/2 ms-4 -translate-y-1/2 rounded bg-gray-900 px-2 py-1.5 text-xs font-medium text-white hidden group-hover:block;
}

.menu-item {
  @apply aspect-square relative place-items-center flex justify-center rounded px-2 py-1.5 outline-none
    text-gray-500 hover:bg-gray-50 hover:text-gray-700
    dark:text-gray-300 dark:hover:bg-gray-900 dark:hover:text-gray-100;
}

.menu-item.menu-item-selected {
  @apply text-blue-500 dark:text-blue-500 dark:hover:text-blue-400;
}

.logout {
  @apply relative flex w-full justify-center rounded-lg px-2 py-1.5 text-sm text-gray-500 hover:bg-gray-50 hover:text-gray-700
}

.side-menu {
  /*
  background-color: #fde68a;
  background-image: url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23fcd34d' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E");
  */
}
</style>
